import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, TrendUp, Fire, SealCheck } from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { artists, trendingTags, posts } from "@/data/mockData";
import { api } from "@/lib/api";

export default function Search() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ users: [], posts: [] });

  useEffect(() => {
    if (!q.trim()) {
      setResults({ users: [], posts: [] });
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/search", { params: { q } })
        .then(({ data }) => setResults({ users: data.users || [], posts: data.posts || [] }))
        .catch(() => setResults({ users: [], posts: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const filtered = q ? results.users : artists;
  const artImages = q ? results.posts.filter((p) => p.media_url).slice(0, 9) : posts.filter((p) => p.image).slice(0, 9);

  return (
    <Layout wide>
      <div data-testid="search-page">
        <header className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] p-4 md:p-6">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
            BUSCADOR GLOBAL
          </div>
          <div className="flex items-center gap-3 border border-[#27272A] bg-[#0a0a0a] px-4 focus-within:border-[#FF3E00]">
            <MagnifyingGlass size={20} className="text-[#A1A1AA]" />
            <input
              data-testid="global-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="buscar artistas, disciplinas, hashtags, obras..."
              className="flex-1 bg-transparent py-4 focus:outline-none font-mono text-base"
              autoFocus
            />
            {q ? (
              <button
                onClick={() => setQ("")}
                className="font-mono text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-white"
              >
                ✕
              </button>
            ) : null}
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-10">
          {/* Trending */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <TrendUp size={18} className="text-[#FF3E00]" />
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                Trending
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {trendingTags.map((t) => (
                <button
                  key={t.tag}
                  className="flex items-center gap-2 px-3 py-2 border border-[#27272A] hover:border-[#FF3E00] group transition-colors"
                >
                  <span className="font-display font-semibold text-sm group-hover:text-[#FF3E00]">
                    {t.tag}
                  </span>
                  <span className="font-mono text-[10px] text-[#A1A1AA]">
                    {t.posts}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* People */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Fire size={18} className="text-[#FF3E00]" />
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                {q ? "Resultados" : "Artistas del momento"}
              </h2>
              <span className="ml-auto font-mono text-[10px] text-[#A1A1AA]">
                {filtered.length} encontrados
              </span>
            </div>
            <ul>
              {filtered.slice(0, 8).map((a) => {
                const handle = a.handle || a.slug;
                const photo = a.photo || a.avatar;
                const role = a.role || (a.primary_discipline || "").replace(/-/g, " ").toUpperCase();
                return (
                <li key={handle}>
                  <Link
                    to={`/artista/${handle}`}
                    className="flex items-center gap-4 p-3 border border-[#27272A] -mb-px hover:bg-[#121212] group"
                  >
                    <div className="h-12 w-12 bg-[#121212] border border-[#27272A] shrink-0 overflow-hidden">
                      {photo ? (
                        <img
                          src={photo}
                          alt={a.name}
                          className="h-12 w-12 object-cover grayscale group-hover:grayscale-0 transition-all"
                        />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-display font-bold">{a.name}</span>
                        {a.verified ? (
                          <SealCheck size={13} weight="fill" className="text-[#FF3E00]" />
                        ) : null}
                      </div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                        @{handle} · {role}
                      </div>
                    </div>
                    <span className="font-mono text-[10px] text-[#FF3E00] tracking-wider shrink-0">
                      {(((a.followers || 0)) / 1000).toFixed(1)}K
                    </span>
                  </Link>
                </li>
              );})}
            </ul>
          </section>

          {/* Explore grid */}
          <section>
            <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
              Obras del momento
            </h2>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-1 md:gap-2">
              {artImages.map((p) => (
                <Link
                  key={p.id}
                  to={`/post/${p.id}`}
                  className="aspect-square overflow-hidden bg-[#121212] group"
                >
                  <img
                    src={p.media_url || p.image}
                    alt=""
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                  />
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
